<div class="space-y-4">

  <!-- Loading skeleton -->
  <ng-container *ngIf="isLoading">
    <div class="bg-white rounded-xl border border-gray-100 overflow-hidden animate-pulse">
      <div class="h-24 bg-gray-200"></div>
      <div class="px-6 pb-6 pt-2">
        <div class="w-20 h-20 rounded-xl bg-gray-200 -mt-10 mb-4"></div>
        <div class="h-5 bg-gray-200 rounded w-40 mb-2"></div>
        <div class="h-3 bg-gray-200 rounded w-24 mb-1"></div>
        <div class="h-3 bg-gray-200 rounded w-32"></div>
      </div>
    </div>
    <div class="bg-white rounded-xl border border-gray-100 p-6 animate-pulse space-y-2">
      <div class="h-3 bg-gray-200 rounded w-16 mb-4"></div>
      <div class="h-3 bg-gray-200 rounded w-full"></div>
      <div class="h-3 bg-gray-200 rounded w-5/6"></div>
      <div class="h-3 bg-gray-200 rounded w-4/6"></div>
    </div>
  </ng-container>

  <!-- Profile not found -->
  <div *ngIf="!isLoading && !corpMember && !loadError" class="text-center py-20">
    <mat-icon class="!text-5xl text-gray-300">person_off</mat-icon>
    <p class="text-gray-400 mt-3 text-sm">Profile not found.</p>
  </div>

  <!-- Failed to load -->
  <div *ngIf="!isLoading && loadError" class="text-center py-20">
    <mat-icon class="!text-5xl text-red-300">wifi_off</mat-icon>
    <p class="text-gray-500 mt-3 font-medium">Failed to load this profile.</p>
    <p class="text-gray-400 text-sm mt-1">Something went wrong. Please check your connection and try again.</p>
    <button type="button" mat-stroked-button color="primary" (click)="loadProfile()" class="mt-4">
      <mat-icon>refresh</mat-icon>
      Retry
    </button>
  </div>

  <!-- Profile content -->
  <ng-container *ngIf="!isLoading && corpMember">

    <!-- Hero card -->
    <div class="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
      <div class="h-24 bg-[#DFFF80]"></div>
      <div class="px-6 pb-6">
        <div class="-mt-12 mb-4">
          <img
            *ngIf="corpMember.Media?.url"
            [ngSrc]="corpMember.Media?.url!"
            width="80"
            height="80"
            alt="Profile photo"
            class="w-20 h-20 rounded-xl object-cover border-4 border-white shadow-sm"
          />
          <div
            *ngIf="!corpMember.Media?.url"
            class="w-20 h-20 rounded-xl bg-[#1A433A] border-4 border-white shadow-sm flex items-center justify-center"
          >
            <span class="text-[#DFFF80] text-2xl font-bold select-none">
              {{ corpMember.first_name?.toUpperCase()?.charAt(0) || '&middot;' }}
            </span>
          </div>
        </div>

        <h1 class="text-xl font-semibold text-[#1A433A] leading-tight">
          {{ corpMember._fullname }}
        </h1>
        <p *ngIf="corpMember.nickname" class="text-sm text-gray-400 mt-0.5">
          @{{ corpMember.nickname }}
        </p>

        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-gray-500">
          <span class="flex items-center gap-0.5">
            <mat-icon inline class="text-base align-middle">flag</mat-icon>
            {{ corpMember._location }}
          </span>
          <span>·</span>
          <span>Joined {{ corpMember.time_with_us }}</span>
        </div>

        <button
          *ngIf="showStartChat"
          type="button"
          mat-flat-button
          (click)="startChat()"
          class="!bg-[#1A433A] !text-white !mt-4"
        >
          <mat-icon>chat_bubble_outline</mat-icon>
          Chat with {{ corpMember.nickname ?? corpMember.first_name }}
        </button>
      </div>
    </div>

    <!-- Bio -->
    <div *ngIf="corpMember.bio" class="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
      <p class="text-xs font-semibold text-[#1A433A] uppercase tracking-widest mb-3">About</p>
      <p class="text-gray-700 text-sm leading-relaxed">{{ corpMember.bio }}</p>
    </div>

    <!-- NYSC Service details -->
    <div *ngIf="hasServiceDetails" class="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
      <p class="text-xs font-semibold text-[#1A433A] uppercase tracking-widest mb-4">NYSC Service</p>
      <div class="grid grid-cols-2 gap-4">

        <div *ngIf="corpMember.PPA?.name" class="flex items-start gap-2">
          <mat-icon class="text-[#1A433A] shrink-0 !text-xl mt-0.5">business</mat-icon>
          <div>
            <p class="text-xs text-gray-400">Place of Assignment</p>
            <p class="text-sm font-medium text-gray-800">{{ corpMember.PPA?.name }}</p>
            <p *ngIf="corpMember.PPA?.type_of_ppa" class="text-xs text-gray-500">{{ corpMember.PPA?.type_of_ppa }}</p>
          </div>
        </div>

        <div *ngIf="corpMember.batch || corpMember.stream" class="flex items-start gap-2">
          <mat-icon class="text-[#1A433A] shrink-0 !text-xl mt-0.5">school</mat-icon>
          <div>
            <p class="text-xs text-gray-400">Batch / Stream</p>
            <p class="text-sm font-medium text-gray-800">
              <span *ngIf="corpMember.batch">Batch {{ corpMember.batch }}</span>
              <span *ngIf="corpMember.batch && corpMember.stream"> · </span>
              <span *ngIf="corpMember.stream">Stream {{ corpMember.stream }}</span>
            </p>
          </div>
        </div>

        <div *ngIf="corpMember.travel_from_state" class="flex items-start gap-2">
          <mat-icon class="text-[#1A433A] shrink-0 !text-xl mt-0.5">home</mat-icon>
          <div>
            <p class="text-xs text-gray-400">From</p>
            <p class="text-sm font-medium text-gray-800">
              <span *ngIf="corpMember.travel_from_city">{{ corpMember.travel_from_city }}, </span>
              {{ corpMember.travel_from_state }}
            </p>
          </div>
        </div>

      </div>
    </div>

    <!-- External links -->
    <div *ngIf="externalLinks && externalLinks.length > 0" class="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
      <p class="text-xs font-semibold text-[#1A433A] uppercase tracking-widest mb-4">Social Links</p>
      <div class="flex flex-wrap gap-2">
        <a
          *ngFor="let link of externalLinks"
          [href]="link.url"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:border-[#1A433A] hover:text-[#1A433A] transition-colors no-underline"
        >
          <mat-icon inline class="text-base">open_in_new</mat-icon>
          {{ link.label }}
        </a>
      </div>
    </div>

  </ng-container>
</div>
